import { DateInputLocalized, InputNumberLocalized, MonthSelect, ProcessingOverlay } from '@components'

import { Color, FormItemsGroup, Link, Text } from '@billy/design-system'
import React, { ReactElement } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useFormContext } from '../../../../../../hooks'
import { useOrganizationAccountingSettings } from '../../contexts/organizationAccountingSettingsContext'
import { useShouldShowAccountingBasis } from '../../hooks/useShouldShowAccountingBasis'
import { OrganizationAccountingFormSchema } from '../../utils/organizationAccountingSettingsFormData'
import { AccountingBasisFormItem } from './elements/AccountingBasisFormItem'
import { GermanAccountingBasisFormItem } from './elements/GermanAccountingBasisFormItem'
import { SalesTaxPeriodFormItem } from './elements/SalesTaxPeriodFormItem'
import { TaxationTypeFormItem } from './elements/TaxationTypeFormItem'
import * as Styled from './styles'

/* Potentially will be created for other markets */
const FISCAL_YEAR_HELP_ARTICLE_URL_DK =
  'https://help.shine.co/da/articles/625868-start-et-nyt-regnskabsar-i-shine-regnskab'

export const TaxSettings = (): ReactElement => {
  const { t } = useTranslation()
  const { FormItem } = useFormContext<OrganizationAccountingFormSchema>()
  const { organization } = useUserOrganization()
  const { isProcessing, isLoading } = useOrganizationAccountingSettings()
  const { shouldShowAccountingBasis } = useShouldShowAccountingBasis()

  const isGermanOrganization = organization?.countryId === 'DE'
  const isDanishOrganization = organization?.countryId === 'DK'

  return (
    <Styled.FormFieldsWrapper>
      {(isProcessing || isLoading) && <ProcessingOverlay color={Color.White} />}
      <FormItemsGroup>
        <FormItem
          name="fiscalYearEndMonth"
          label={t('settings.organization.accounting.fiscal_year_end')}
          render={({ field: { onChange, value, ...rest } }) => (
            <MonthSelect selectedId={String(value)} onSelect={onChange} {...rest} />
          )}
        />
        <FormItem
          name="firstFiscalYearStart"
          label={t('settings.organization.accounting.fiscal_year_date_start')}
          render={({ field }) => <DateInputLocalized {...field} />}
        />
        <FormItem
          name="firstFiscalYearEnd"
          label={t('settings.organization.accounting.first_fiscal_year_end')}
          render={({ field }) => <InputNumberLocalized {...field} keyboard step={1} withFormatting={false} />}
        />
        {isDanishOrganization && (
          <Text colorVariant="secondary">
            <Trans i18nKey="settings.organization.accounting.help_text">
              Er du i tvivl?
              <Link href={FISCAL_YEAR_HELP_ARTICLE_URL_DK} target="_blank" rel="noopener noreferrer" withAnimation>
                Læs vores hjælpeartikel om regnskabsår
              </Link>
            </Trans>
          </Text>
        )}
        <SalesTaxPeriodFormItem />
        <FormItem
          name="fiscalLockingDate"
          extra={t('settings.organization.accounting.fiscal_locking_date_tip')}
          label={t('settings.organization.accounting.fiscal_locking_date')}
          render={({ field }) => <DateInputLocalized allowClear {...field} />}
        />
        {shouldShowAccountingBasis && <AccountingBasisFormItem />}
        {isGermanOrganization && (
          <>
            <GermanAccountingBasisFormItem />
            <TaxationTypeFormItem />
          </>
        )}
      </FormItemsGroup>
    </Styled.FormFieldsWrapper>
  )
}
